/**
 * Admin Blog Tab - Manage blog posts and submissions
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Plus, Edit, Trash2, Eye, EyeOff, Check, X, Clock, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminBlogTab = () => {
  const { blogPosts, loadData } = useAdmin();
  
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "", slug: "", excerpt: "", content: "", category: "", tags: "", featured_image: "", is_published: false
  });
  const [savingPost, setSavingPost] = useState(false);
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingSubmission, setRejectingSubmission] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      const res = await axios.get(`${API}/blog/submissions`, { withCredentials: true });
      setSubmissions(res.data);
    } catch (err) {
      console.error("Failed to load submissions:", err);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const resetBlogForm = () => {
    setBlogForm({ title: "", slug: "", excerpt: "", content: "", category: "", tags: "", featured_image: "", is_published: false });
    setEditingPost(null);
    setShowBlogForm(false);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      category: post.category || "",
      tags: Array.isArray(post.tags) ? post.tags.join(", ") : (post.tags || ""),
      featured_image: post.featured_image || "",
      is_published: post.is_published || false
    });
    setShowBlogForm(true);
  };

  const handleSavePost = async () => {
    if (!blogForm.title || !blogForm.content) {
      toast.error("Please fill in title and content");
      return;
    }
    setSavingPost(true);
    try {
      const postData = {
        ...blogForm,
        slug: blogForm.slug || blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        tags: blogForm.tags ? blogForm.tags.split(",").map(t => t.trim()).filter(t => t) : []
      };

      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.post_id}`, postData, { withCredentials: true });
        toast.success("Post updated successfully");
      } else {
        await axios.post(`${API}/blog`, postData, { withCredentials: true });
        toast.success("Post created successfully");
      }
      resetBlogForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save post");
    } finally {
      setSavingPost(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, { withCredentials: true });
      toast.success("Post deleted");
      loadData();
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const handleTogglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.post_id}`, { is_published: !post.is_published }, { withCredentials: true });
      toast.success(post.is_published ? "Post unpublished" : "Post published");
      loadData();
    } catch {
      toast.error("Failed to update post");
    }
  };

  // Submission handlers
  const handleApproveSubmission = async (submissionId) => {
    try {
      await axios.put(`${API}/blog/submissions/${submissionId}/approve`, {}, { withCredentials: true });
      toast.success("Submission approved and published!");
      loadSubmissions();
      loadData();
      setViewingSubmission(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve submission");
    }
  };

  const handleRejectSubmission = async () => {
    if (!rejectingSubmission) return;
    try {
      await axios.put(`${API}/blog/submissions/${rejectingSubmission}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success("Submission rejected");
      setShowRejectDialog(false);
      setRejectReason("");
      setRejectingSubmission(null);
      loadSubmissions();
      setViewingSubmission(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject submission");
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/blog/submissions/${submissionId}`, { withCredentials: true });
      toast.success("Submission deleted");
      loadSubmissions();
    } catch {
      toast.error("Failed to delete submission");
    }
  };

  const pendingSubmissions = submissions.filter(s => s.status === "pending");
  const reviewedSubmissions = submissions.filter(s => s.status !== "pending");

  const getStatusBadge = (status) => {
    switch(status) {
      case "pending": return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case "approved": return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" /> Approved</Badge>;
      case "rejected": return <Badge className="bg-red-100 text-red-700"><X className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <>
      <Tabs defaultValue="posts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="posts">
            <BookOpen className="w-4 h-4 mr-2" /> Published Posts ({blogPosts.length})
          </TabsTrigger>
          <TabsTrigger value="submissions">
            <FileText className="w-4 h-4 mr-2" /> Submissions
            {pendingSubmissions.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{pendingSubmissions.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Published Posts Tab */}
        <TabsContent value="posts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Blog Posts</CardTitle>
                <CardDescription>Create and manage blog content</CardDescription>
              </div>
              <Button onClick={() => { resetBlogForm(); setShowBlogForm(true); }} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" /> New Post
              </Button>
            </CardHeader>
            <CardContent>
              {blogPosts.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-600">No blog posts yet</p>
                  <p className="text-sm text-stone-500 mt-1">Click "New Post" to create your first blog post</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blogPosts.map(post => (
                      <TableRow key={post.post_id}>
                        <TableCell className="font-medium">{post.title}</TableCell>
                        <TableCell>{post.category || "-"}</TableCell>
                        <TableCell className="text-sm">{post.author || "CatPals+ Team"}</TableCell>
                        <TableCell>
                          <Badge variant={post.is_published ? "default" : "secondary"}>
                            {post.is_published ? "Published" : "Draft"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{new Date(post.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleTogglePublished(post)}>
                              {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleEditPost(post)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeletePost(post.post_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submissions Tab */}
        <TabsContent value="submissions">
          <Card>
            <CardHeader>
              <CardTitle>Community Submissions</CardTitle>
              <CardDescription>Review and approve blog posts submitted by customers, volunteers, and staff</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSubmissions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                  <p className="mt-2 text-stone-600">Loading submissions...</p>
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-600">No submissions yet</p>
                  <p className="text-sm text-stone-500 mt-1">Submissions from the community will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingSubmissions.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600" /> Pending Review ({pendingSubmissions.length})
                      </h3>
                      <div className="space-y-3">
                        {pendingSubmissions.map(sub => (
                          <div key={sub.submission_id} className="border rounded-lg p-4 bg-amber-50/50 hover:bg-amber-50 transition-colors">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-stone-900">{sub.title}</h4>
                                <p className="text-sm text-stone-600 mt-1">
                                  By <span className="font-medium">{sub.author_name}</span> ({sub.author_type}) • {sub.category}
                                </p>
                                <p className="text-xs text-stone-500 mt-1">
                                  Submitted {new Date(sub.created_at).toLocaleString()}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setViewingSubmission(sub)}>
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </Button>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveSubmission(sub.submission_id)}>
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => { setRejectingSubmission(sub.submission_id); setShowRejectDialog(true); }}>
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {reviewedSubmissions.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-stone-700 mb-3">Previously Reviewed ({reviewedSubmissions.length})</h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Author</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reviewedSubmissions.map(sub => (
                            <TableRow key={sub.submission_id}>
                              <TableCell className="font-medium">{sub.title}</TableCell>
                              <TableCell className="text-sm">{sub.author_name}</TableCell>
                              <TableCell>{getStatusBadge(sub.status)}</TableCell>
                              <TableCell className="text-sm">{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => setViewingSubmission(sub)}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteSubmission(sub.submission_id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Blog Post Editor Dialog */}
      <Dialog open={showBlogForm} onOpenChange={() => setShowBlogForm(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Post" : "New Blog Post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input 
                  value={blogForm.title}
                  onChange={e => setBlogForm({...blogForm, title: e.target.value})}
                  placeholder="Post title"
                />
              </div>
              <div>
                <Label>Slug</Label>
                <Input 
                  value={blogForm.slug}
                  onChange={e => setBlogForm({...blogForm, slug: e.target.value})}
                  placeholder="auto-generated-from-title"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input 
                  value={blogForm.category}
                  onChange={e => setBlogForm({...blogForm, category: e.target.value})}
                  placeholder="e.g., Cat Care, News, Stories"
                />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input 
                  value={blogForm.tags}
                  onChange={e => setBlogForm({...blogForm, tags: e.target.value})}
                  placeholder="e.g., kittens, adoption, tips"
                />
              </div>
            </div>
            <div>
              <Label>Excerpt</Label>
              <Input 
                value={blogForm.excerpt}
                onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})}
                placeholder="Brief summary of the post"
              />
            </div>
            <div>
              <Label>Featured Image URL</Label>
              <Input 
                value={blogForm.featured_image}
                onChange={e => setBlogForm({...blogForm, featured_image: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={blogForm.content}
                onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                className="bg-white min-h-[300px] font-mono text-sm"
                placeholder="Write your blog post content here. You can use basic HTML tags like <b>, <i>, <a>, <p>, <h2>, <ul>, <li> for formatting."
              />
              <p className="text-xs text-stone-500 mt-1">Tip: Use HTML tags for formatting (e.g., &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;a href="..."&gt;link&lt;/a&gt;)</p>
            </div>
            <div className="flex items-center gap-2 pt-4">
              <input 
                type="checkbox"
                id="is_published"
                checked={blogForm.is_published}
                onChange={e => setBlogForm({...blogForm, is_published: e.target.checked})}
              />
              <label htmlFor="is_published" className="text-sm">Publish immediately</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlogForm(false)}>Cancel</Button>
            <Button onClick={handleSavePost} disabled={savingPost} className="bg-amber-600 hover:bg-amber-700">
              {savingPost ? "Saving..." : (editingPost ? "Update Post" : "Create Post")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={!!viewingSubmission} onOpenChange={() => setViewingSubmission(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Submission</DialogTitle>
          </DialogHeader>
          {viewingSubmission && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                {getStatusBadge(viewingSubmission.status)}
                <span className="text-sm text-stone-500">
                  Submitted {new Date(viewingSubmission.created_at).toLocaleString()}
                </span>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-stone-50 rounded-lg">
                <div>
                  <Label className="text-xs text-stone-500">Author</Label>
                  <p className="font-medium">{viewingSubmission.author_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Email</Label>
                  <p>{viewingSubmission.author_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Type</Label>
                  <p className="capitalize">{viewingSubmission.author_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Category</Label>
                  <p>{viewingSubmission.category}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-stone-500">Title</Label>
                <h3 className="text-xl font-bold text-stone-900">{viewingSubmission.title}</h3>
              </div>

              {viewingSubmission.featured_image && (
                <div>
                  <Label className="text-xs text-stone-500">Featured Image</Label>
                  <img 
                    src={viewingSubmission.featured_image} 
                    alt="Featured" 
                    className="mt-2 max-h-48 rounded-lg object-cover"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs text-stone-500">Content</Label>
                <div className="mt-2 p-4 bg-white border rounded-lg max-h-64 overflow-y-auto">
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {viewingSubmission.content}
                  </div>
                </div>
              </div>

              {viewingSubmission.admin_notes && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Label className="text-xs text-red-600">Rejection Reason</Label>
                  <p className="text-red-700">{viewingSubmission.admin_notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingSubmission(null)}>Close</Button>
            {viewingSubmission?.status === "pending" && (
              <>
                <Button variant="destructive" onClick={() => { setRejectingSubmission(viewingSubmission.submission_id); setShowRejectDialog(true); }}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleApproveSubmission(viewingSubmission.submission_id)}>
                  <Check className="w-4 h-4 mr-1" /> Approve & Publish
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={() => { setShowRejectDialog(false); setRejectReason(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for rejection (optional)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide feedback to the author..."
              rows={4}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectSubmission}>
              Reject Submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminBlogTab;
